// Production Queue. Lists orders that are paid or in_production. Each card
// shows the latest production event + buttons to log the next one. Top-of-
// page tab strip filters by current production stage (last event kind).
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'

interface QueueRow {
  id: string; number: number; status: string; totalCents: number; createdAt: string
  paidAt: string | null; designKind: string | null; approvalStatus: string | null
  itemCount: number; lastEventKind: string | null
  customerEmail: string | null; shippingName: string | null
}

const STAGES = ['in_print','pressed','qc','packed'] as const
type Stage = typeof STAGES[number]

// Tab strip definitions. `_none` filters to rows with no production event
// logged yet — the "fresh on the floor" bucket.
const FILTERS: Array<{ key: '' | '_none' | Stage; label: string }> = [
  { key: '',         label: 'All' },
  { key: '_none',    label: 'Not started' },
  { key: 'in_print', label: 'In Print' },
  { key: 'pressed',  label: 'Pressed' },
  { key: 'qc',       label: 'QC' },
  { key: 'packed',   label: 'Packed' },
]

export default function ProductionQueue() {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<QueueRow[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [stage, setStage] = useState<'' | '_none' | Stage>('')

  // Per-stage counts for the tab badges. "Not started" is everything that
  // isn't sitting in one of the four stage buckets — so the five tabs sum
  // to exactly the All total (an order's last event could be a non-stage
  // kind like 'paid' or a free-text note, and those still need to surface).
  const counts = useMemo(() => {
    const c: Record<string, number> = { '': items.length, _none: 0, in_print: 0, pressed: 0, qc: 0, packed: 0 }
    const STAGE_SET: ReadonlySet<string> = new Set(STAGES)
    for (const r of items) {
      if (r.lastEventKind && STAGE_SET.has(r.lastEventKind)) c[r.lastEventKind]++
    }
    c._none = items.length - c.in_print - c.pressed - c.qc - c.packed
    return c
  }, [items])

  // Apply the active filter to the queue before rendering. "Not started"
  // mirrors the count rule: any row whose last event isn't a stage kind.
  const visible = useMemo(() => {
    if (stage === '') return items
    if (stage === '_none') {
      const STAGE_SET: ReadonlySet<string> = new Set(STAGES)
      return items.filter((r) => !r.lastEventKind || !STAGE_SET.has(r.lastEventKind))
    }
    return items.filter((r) => r.lastEventKind === stage)
  }, [items, stage])

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: QueueRow[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/production')
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }, [getAccessToken])
  useEffect(() => { void load() }, [load])

  async function logEvent(id: string, kind: string) {
    setBusy(id + kind)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/production/${id}/event`, {
        method: 'POST', body: JSON.stringify({ kind }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(null) }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="eyebrow">Production</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Production Queue</h1>
        <p className="text-ink-2 mt-2 text-sm max-w-2xl">Paid orders on the floor. Log each stage as it completes — the timeline lives on the order's detail page.</p>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-border pb-1">
        {FILTERS.map((f) => (
          <button key={f.key || 'all'} onClick={() => setStage(f.key)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
              (stage === f.key ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
            {f.label} <span className="ml-1 text-ink-3">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      {items.length === 0 ? (
        <div className="card text-center text-ink-3 text-sm">Queue is empty. Mark an order Paid (and approve its artwork) to start production.</div>
      ) : visible.length === 0 ? (
        <div className="card text-center text-ink-3 text-sm">No orders in this stage.</div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => (
            <div key={r.id} className="card flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/admin/orders/${r.id}`} className="font-mono text-xs text-ink-2 hover:text-gold-dark">#{r.number}</Link>
                  <StatusBadge status={r.status} />
                  {r.approvalStatus && r.approvalStatus !== 'approved' && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Artwork {r.approvalStatus.replace('_', ' ')}</span>
                  )}
                  {r.designKind && <span className="text-[10px] uppercase tracking-wider text-ink-3">{r.designKind}</span>}
                </div>
                <div className="text-ink mt-1 truncate">{r.shippingName ?? r.customerEmail ?? '—'}</div>
                <div className="text-xs text-ink-3 truncate">
                  {r.itemCount} item{r.itemCount === 1 ? '' : 's'} · paid {r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}
                  {r.lastEventKind && <> · last event <strong className="text-ink-2">{r.lastEventKind.replace(/_/g, ' ')}</strong></>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold text-ink">${(r.totalCents / 100).toFixed(2)}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map((s) => (
                  <button key={s} disabled={busy === r.id + s} onClick={() => logEvent(r.id, s)}
                    className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide rounded border border-border text-ink-2 hover:border-gold hover:bg-gold/5 disabled:opacity-50 transition-colors">
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
